import React, { FC, useCallback, useState } from 'react';
import SubMenu from 'antd/lib/menu/SubMenu';
import { Menu, Skeleton, confirm } from 'src/common';
import { useWorkspaces } from 'src/models/workspace/hooks';
import styles from './index.module.scss';
import WorkspaceItem from './WorkspaceItem';
import { useTranslation } from 'react-i18next';
import { IBidIdentifier, ILinkedBidIdentifier } from 'src/models/bids/Bids/types';
import classNames from 'classnames';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BidItemType } from '@tendium/prom-types/tender';
import modalStyles from 'src/common/Modal/index.module.scss';
import CreateWorkspaceModal from 'src/shared/Workspace/CreateWorkspaceModal';
import { useMoveToWorkspace } from 'src/models/bids/Bids/hooks';

interface IProps {
  linkedBids: ILinkedBidIdentifier[];
  origins: string[];
  bids: IBidIdentifier[];
  eventSource: string;
  setIsUpdating: (val: boolean) => void;
  onCloseDropDown: () => void;
  onChange?: () => void;
}

export const CreateNewBid: FC<IProps> = props => {
  const { linkedBids, origins: originIds, bids, setIsUpdating, onChange, onCloseDropDown, eventSource } = props;
  const { t } = useTranslation();
  const { data, loading } = useWorkspaces();
  const [moveToWorkspace, { loading: isLoading }] = useMoveToWorkspace();

  const workspaces = data && data.filter(ws => ws.isActive).sort((a, b) => a.name.localeCompare(b.name, 'sv'));

  const onFinish = useCallback(() => {
    onCloseDropDown();
    !!onChange && onChange();
  }, [onCloseDropDown, onChange]);

  const onMoveToWorkspace = useCallback(
    async (workspaceId: string) => {
      setIsUpdating(true);
      moveToWorkspace({
        workspaceId,
        eventSource,
        originType: BidItemType.CallOff,
        originIds,
        bids,
        linkedBids
      });
      onFinish();
      setIsUpdating(false);
    },
    [bids, eventSource, linkedBids, moveToWorkspace, onFinish, originIds, setIsUpdating]
  );

  const onConfirm = useCallback(
    onOK => {
      if (!!linkedBids.length) {
        confirm.confirm({
          title: t('Common.warning'),
          okText: t('Common.confirm'),
          content: <p className={styles.desc}>{t('BidSpaces.bidCreationWarning')}</p>,
          onOk: onOK,
          cancelText: t('Common.cancel'),
          className: modalStyles.confirmModal,
          centered: true
        });
      } else {
        onOK();
      }
    },
    [linkedBids.length, t]
  );

  const onClickWSItem = useCallback(
    (workspaceId: string) => {
      onFinish();
      onConfirm(() => onMoveToWorkspace(workspaceId));
    },
    [onConfirm, onFinish, onMoveToWorkspace]
  );

  const [wsModalVisible, setWsModalVisible] = useState(false);
  const onCreatingWSFinish = useCallback(
    (workspaceId: string | null) => {
      setWsModalVisible(false);
      workspaceId && onMoveToWorkspace(workspaceId);
    },
    [onMoveToWorkspace]
  );

  const onCreateWorkspace = useCallback(() => {
    onConfirm(() => {
      onCloseDropDown();
      setWsModalVisible(true);
    });
  }, [onCloseDropDown, onConfirm]);

  return (
    <>
      {workspaces && (
        <>
          <Menu triggerSubMenuAction={'click'} className={styles.menu}>
            <SubMenu
              title={t('BidSpaces.createNewBid')}
              className={styles.subMenu}
              key={'createNewBid'}
              popupClassName={styles.subMenuOverlay}
            >
              <Menu.Item className={styles.menuItem} key={'createNewBidItem'}>
                <div className={styles.title}>{t('BidSpaces.addBidTo')}</div>
                {workspaces.map(ws => (
                  <div key={ws.id}>
                    <WorkspaceItem ws={ws} loading={!!isLoading} onClick={() => onClickWSItem(ws.id)} />
                    <Skeleton className={styles.menuItemLoading} loading={loading} paragraph={false} active={loading} />
                  </div>
                ))}
                <div className={classNames(styles.wsMenuFooter, { [styles.noWs]: !workspaces || !workspaces.length })}>
                  <button onClick={onCreateWorkspace} className={styles.wsMenuAction}>
                    <span className={styles.wsMenuActionTitle}>{t('BidSpaces.addNewWS')}</span>
                    <FontAwesomeIcon icon={faAngleRight} className={styles.wsMenuActionIcon} />
                  </button>
                </div>
              </Menu.Item>
            </SubMenu>
          </Menu>
          {wsModalVisible && (
            <CreateWorkspaceModal
              visible={wsModalVisible}
              onCancel={() => setWsModalVisible(false)}
              onFinish={onCreatingWSFinish}
              loading={!!isLoading}
            />
          )}
        </>
      )}
    </>
  );
};

export default CreateNewBid;
